// src/pages/CommissionReportPage.js
//
// Commission Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents the employee commission report for a selected period.
//
// The report supports:
//
//   1) LINEAR commission
//   2) TIERED (progressive / marginal) commission
//   3) UNASSIGNED rows (visits without valid employee assignment)
//
// ============================================================================
// IMPORTANT ARCHITECTURAL UPDATE
// ============================================================================
// This file is now integrated with the centralized "evaluation period"
// framework introduced for the application.
//
// Instead of manually editing two date pickers ("from" / "to") directly
// in the toolbar, this page now uses:
//
//   - useEvaluationPeriod()
//   - <EvaluationPeriodPicker />
//
// That gives the user a more professional and faster way to work with time
// ranges such as:
//
//   - Month
//   - Year
//   - Week
//   - Day
//   - Manual
//
// and allows moving backward / forward through periods with arrow controls.
//
// ============================================================================
// WHY THIS CHANGE IS IMPORTANT
// ============================================================================
// In many report pages the old approach:
//
//   From: [date]
//   To:   [date]
//
// becomes inconvenient when users want to analyze:
//
//   - previous months,
//   - previous weeks,
//   - entire years,
//   - repeated report runs over neighboring periods.
//
// The new framework solves that by centralizing date-range behavior and
// making report pages easier to maintain and more consistent.
//
// ============================================================================
// WHAT THIS PAGE DOES
// ============================================================================
// 1) Loads employees for multi-select filtering (only for non-"user" roles).
// 2) Uses centralized evaluation period state to derive actual `from` / `to`.
// 3) Fetches commission report data from backend.
// 4) Renders per-employee report cards.
// 5) Shows tier breakdown when employee commission mode is TIERED.
// 6) Exports the report to PDF.
//
// ============================================================================
// BACKEND EXPECTATIONS
// ============================================================================
// This UI expects the backend report payload to look like:
//
// {
//   data: [
//     {
//       employee: "John Smith",
//       total_service_value: 12345.67,
//       total_commission: 987.65,
//       commission_mode: "LINEAR" | "TIERED" | "UNASSIGNED",
//       effective_commission_rate: 8.00,
//       tier_breakdown: [
//         {
//           min_amount: "0.00",
//           max_amount: "5000.00",
//           rate: "5.00",
//           base: "5000.00",
//           commission: "250.00"
//         }
//       ],
//       services: [
//         {
//           service: "Massage",
//           total_service_value: 2500.00,
//           commission_percentage: 8.00,
//           commission_value: 200.00
//         }
//       ]
//     }
//   ],
//   grandTotals: {
//     total_service_value: "12345.67",
//     total_commission: "987.65"
//   }
// }
//
// ============================================================================
// FRONTEND / UX NOTES
// ============================================================================
// - The page keeps its own layout and scroll behavior.
// - We do NOT rely on global body/html overflow rules.
// - Toolbar remains fixed in normal page flow.
// - Report content scrolls inside the local scroll container.
//
// ============================================================================

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchCommissionReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import "./CommissionReportPage.css";

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format money-like values with fixed 2 decimals.
 *
 * We intentionally keep formatting simple and deterministic.
 * This matches the style already used in the reporting part of the app.
 *
 * Why fixed decimals?
 * - tables are easier to scan,
 * - totals align visually,
 * - PDF export remains compact and stable,
 * - this avoids locale-related surprises in financial-like views.
 */
const formatMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

/**
 * Format percentage-like values with fixed 2 decimals.
 *
 * This is especially important in TIERED mode, where we often display
 * an "effective rate" rather than a single configured commission percent.
 */
const formatPercent = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

/**
 * Normalize commission mode for safe UI rendering.
 *
 * This protects the page from:
 * - null / undefined,
 * - unexpected casing,
 * - future backend changes.
 *
 * We prefer to keep the UI resilient instead of assuming the backend always
 * returns perfectly normalized mode strings.
 */
const normalizeMode = (mode) => {
  const m = String(mode || "").toUpperCase();

  if (m === "TIERED") return "TIERED";
  if (m === "LINEAR") return "LINEAR";
  if (m === "UNASSIGNED") return "UNASSIGNED";

  return m || "UNKNOWN";
};

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const CommissionReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  /**
   * Current user role and id.
   *
   * We derive these once for readability because they influence:
   * - which evaluation period modes are allowed,
   * - whether employee picker is editable,
   * - what default userIds filter should be used.
   */
  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;
  const canExportPdf = currentRole === "superuser";

  /**
   * Role-based allowed evaluation modes.
   *
   * BUSINESS RULE
   * -------------
   * - normal "user" should see only MONTH
   * - higher roles may choose other period types
   *
   * Why this lives here:
   * - the hook is reusable and generic,
   * - this page decides the business-level permission model.
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  /**
   * Shared evaluation-period controller.
   *
   * IMPORTANT:
   * This follows the same explicit pattern as the working Income Report page:
   *
   *   const evaluationPeriod = useEvaluationPeriod({
   *     defaultMode: ...,
   *     initialDate: ...,
   *     allowedModes: ...,
   *   });
   *
   * This makes the implementation easier to compare across report pages
   * and makes future refactoring safer.
   */
  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  /**
   * These are the resolved backend-friendly dates derived by the shared
   * evaluation-period framework.
   *
   * Example:
   * - mode = MONTH
   * - display = April 2026
   *
   * Then:
   *   selectedFrom = "2026-04-01"
   *   selectedTo   = "2026-04-30"
   *
   * The backend still receives a classic from/to API contract.
   *
   * This destructuring is intentionally explicit because it mirrors the already
   * proven Income Report pattern and makes the code easier to follow.
   */
  const {
    from: selectedFrom,
    to: selectedTo,
  } = evaluationPeriod;

  /**
   * Report-specific local filters.
   *
   * IMPORTANT:
   * Date range is no longer stored here.
   * The shared evaluation-period framework owns that state.
   *
   * This local filter object now only keeps:
   * - selected employees (for admin / superuser style views)
   *
   * For plain "user" role, we initialize userIds with the current user's id.
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
  }));

  /**
   * Main report dataset returned by backend.
   *
   * Shape expected:
   *   [
   *     {
   *       employee,
   *       total_service_value,
   *       total_commission,
   *       commission_mode,
   *       effective_commission_rate,
   *       tier_breakdown,
   *       services: [...]
   *     }
   *   ]
   */
  const [reportData, setReportData] = useState([]);

  /**
   * Grand totals returned by backend.
   */
  const [grandTotals, setGrandTotals] = useState({
    total_service_value: 0,
    total_commission: 0,
  });

  /**
   * This state stores the ACTUAL period that was last used to fetch the
   * currently displayed dataset.
   *
   * Why do we need this?
   * Because the user may:
   *   1) load report for April
   *   2) move picker to May
   *   3) not click Generate yet
   *
   * In that case:
   * - visible data is still April data
   * - picker currently shows May
   *
   * PDF export should describe the LOADED dataset, not merely the current
   * editor state of the picker.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /**
   * Generic page state.
   */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Available employees for multi-select.
   * Used only for non-user roles.
   */
  const [users, setUsers] = useState([]);

  /**
   * PDF orientation selector.
   *
   * Supported values:
   * - "p" = portrait
   * - "l" = landscape
   */
  const [pdfOrientation, setPdfOrientation] = useState("p");

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for normal user role                                */
  /* ------------------------------------------------------------------------ */

  /**
   * If current user role is "user", the backend should always receive only
   * that user's id as filter.
   *
   * We enforce that here so the local state never drifts.
   *
   * This protects the page from edge cases such as:
   * - delayed auth context hydration,
   * - page refresh after login,
   * - switching from empty initial state to a fully loaded user object.
   */
  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * Higher roles can filter the report by multiple employees.
   * Therefore we load the employee list only when the logged-in role is not
   * a plain "user".
   */
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[CommissionReport] ❌ Failed to fetch users:", err);
        setError(t("reports:commission.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ------------------------------------------------------------------------ */
  /* Derived display helpers                                                  */
  /* ------------------------------------------------------------------------ */

  /**
   * Human-readable display for the current logged-in user.
   *
   * Used in the read-only Employee field shown when role === "user".
   * This keeps JSX cleaner and avoids assembling the same string inline.
   */
  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report data from backend                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * One stable async loader used both:
   * - on first mount
   * - on Generate click
   *
   * This mirrors the Income Report pattern:
   *
   *   loadReportByRange({
   *     from,
   *     to,
   *     ...
   *   })
   *
   * rather than letting the function silently depend on current component state.
   *
   * That makes it:
   * - easier to reason about,
   * - easier to debug,
   * - easier to reuse later,
   * - less fragile when the page evolves.
   */
  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetchCommissionReport(from, to, userIds);

        setReportData(Array.isArray(response?.data) ? response.data : []);
        setGrandTotals(
          response?.grandTotals || {
            total_service_value: 0,
            total_commission: 0,
          }
        );

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[CommissionReport] ❌ Error fetching data:", err);
        setError(t("reports:commission.errors.failedToFetch"));
        setReportData([]);
        setGrandTotals({
          total_service_value: 0,
          total_commission: 0,
        });
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /* ------------------------------------------------------------------------ */
  /* Initial auto-load                                                        */
  /* ------------------------------------------------------------------------ */

  /**
   * Initial report load:
   * - runs once
   * - uses the initial period resolved by the shared hook
   *
   * Just like Income Report, later picker changes still require explicit
   * Generate click.
   *
   * We keep the initial request in a ref so the first dataset is stable and
   * not accidentally affected by subsequent picker interactions before the
   * user explicitly refreshes the report.
   */
  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived UI hints                                                         */
  /* ------------------------------------------------------------------------ */

  /**
   * Whether at least one employee is in TIERED mode.
   * Used for explanatory hint in the page header.
   */
  const hasTiered = useMemo(() => {
    return (reportData || []).some(
      (r) => normalizeMode(r.commission_mode) === "TIERED"
    );
  }, [reportData]);

  /**
   * Whether there is any currently loaded report data.
   *
   * This is used for:
   * - Export PDF button visibility
   * - empty-state rendering
   */
  const hasReportData = useMemo(() => {
    return Array.isArray(reportData) && reportData.length > 0;
  }, [reportData]);

  /**
   * UI/PDF label for backend commission mode codes.
   *
   * The backend keeps stable technical codes (LINEAR/TIERED/UNASSIGNED),
   * while the frontend translates the label shown to users and printed in PDF.
   */
  const commissionModeLabel = useCallback(
    (mode) =>
      t(`reports:commission.modes.${normalizeMode(mode)}`, {
        defaultValue: normalizeMode(mode),
      }),
    [t]
  );

  /* ------------------------------------------------------------------------ */
  /* PDF export                                                               */
  /* ------------------------------------------------------------------------ */

  const generatePDF = () => {
    const doc = new jsPDF(pdfOrientation, "mm", "a4");

    const from = loadedPeriodMeta?.from || "N/A";
    const to = loadedPeriodMeta?.to || "N/A";
    const displayLabel = loadedPeriodMeta?.displayLabel || "";

    // ----------------------------------------------------------------------
    // Basic document header
    // ----------------------------------------------------------------------
    doc.setFontSize(16);
    doc.text(t("reports:commission.pdfTitle"), 14, 12);

    doc.setFontSize(11);
    doc.text(t("reports:common.fromToPdfLine", { from, to }), 14, 19);

    if (displayLabel) {
      doc.setFontSize(10);
      doc.text(t("reports:common.evaluationPeriodPdfLine", { displayLabel }), 14, 25);
    }

    // Cursor starts lower if we render the extra evaluation-period line
    let cursorY = displayLabel ? 32 : 26;

    if (!Array.isArray(reportData) || reportData.length === 0) {
      doc.setFontSize(12);
      doc.text(t("reports:common.noDataAvailable"), 14, cursorY);
      doc.save(`Commission_Report_${from}_${to}.pdf`);
      return;
    }

    // ----------------------------------------------------------------------
    // We generate one section per employee.
    // Each employee section contains:
    //   1) summary line
    //   2) tier breakdown (only if TIERED)
    //   3) services summary
    //
    // This layout keeps the PDF readable for accounting / managerial review.
    // ----------------------------------------------------------------------
    for (let i = 0; i < reportData.length; i++) {
      const userReport = reportData[i];

      const employeeName = userReport.employee || t("reports:common.unknown", { defaultValue: "Unknown" });
      const mode = normalizeMode(userReport.commission_mode);

      const totalService = formatMoney(userReport.total_service_value || 0);
      const totalCommission = formatMoney(userReport.total_commission || 0);

      /**
       * Effective rate:
       * - prefer explicit backend field,
       * - otherwise derive it from total commission / total service value.
       *
       * This is especially important for TIERED mode because there is no
       * single natural fixed service rate for the whole employee dataset.
       */
      const effectiveRate =
        userReport.effective_commission_rate !== undefined &&
        userReport.effective_commission_rate !== null
          ? formatPercent(userReport.effective_commission_rate)
          : (() => {
              const base = Number(userReport.total_service_value) || 0;
              const comm = Number(userReport.total_commission) || 0;
              if (base <= 0) return "0.00";
              return formatPercent((comm / base) * 100);
            })();

      // Page break guard:
      // if cursor is too low, start a new page for the next employee block
      if (cursorY > 250) {
        doc.addPage();
        cursorY = 18;
      }

      // Employee summary line
      doc.setFontSize(13);
      doc.text(`${employeeName}`, 14, cursorY);

      doc.setFontSize(10);
      doc.text(
        t("reports:commission.pdfEmployeeSummary", { mode: commissionModeLabel(mode), totalService, totalCommission, effectiveRate }),
        14,
        cursorY + 6
      );

      cursorY += 12;

      /* -------------------------------------------------------------------- */
      /* Tier breakdown (only for TIERED)                                     */
      /* -------------------------------------------------------------------- */

      const tierBreakdown = Array.isArray(userReport.tier_breakdown)
        ? userReport.tier_breakdown
        : [];

      if (mode === "TIERED") {
        if (tierBreakdown.length > 0) {
          doc.setFontSize(11);
          doc.text(t("reports:commission.tierBreakdown"), 14, cursorY);

          autoTable(doc, {
            startY: cursorY + 3,
            head: [[t("reports:commission.tierFrom"), t("reports:commission.tierTo"), t("reports:commission.ratePercent"), t("reports:commission.baseInTierPln"), t("reports:commission.commissionPln")]],
            body: tierBreakdown.map((t) => [
              formatMoney(t.min_amount ?? 0),
              t.max_amount === null || t.max_amount === undefined
                ? "∞"
                : formatMoney(t.max_amount),
              formatPercent(t.rate ?? 0),
              formatMoney(t.base ?? 0),
              formatMoney(t.commission ?? 0),
            ]),
            theme: "striped",
            styles: { fontSize: 9 },
            headStyles: { fontStyle: "bold" },
            margin: { left: 14, right: 14 },
          });

          cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;
        } else {
          // Even if backend returns no breakdown, still show a note
          // so the PDF remains self-explanatory.
          doc.setFontSize(10);
          doc.text(
            t("reports:commission.tierBreakdownUnavailablePdf"),
            14,
            cursorY
          );
          cursorY += 8;
        }
      }

      /* -------------------------------------------------------------------- */
      /* Services summary (always)                                            */
      /* -------------------------------------------------------------------- */

      const services = Array.isArray(userReport.services) ? userReport.services : [];

      doc.setFontSize(11);
      doc.text(t("reports:commission.servicesSummary"), 14, cursorY);

      autoTable(doc, {
        startY: cursorY + 3,
        head: [[t("reports:common.service"), t("reports:commission.serviceValuePln"), t("reports:commission.commissionPercent"), t("reports:commission.commissionPln")]],
        body: services.map((s) => [
          String(s.service || t("reports:common.unknown", { defaultValue: "Unknown" })),
          formatMoney(s.total_service_value || 0),
          formatPercent(s.commission_percentage || 0),
          formatMoney(s.commission_value || 0),
        ]),
        theme: "striped",
        styles: { fontSize: 9 },
        headStyles: { fontStyle: "bold" },
        margin: { left: 14, right: 14 },
      });

      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 12;

      // Divider between employees, except after last section
      if (i < reportData.length - 1) {
        doc.setDrawColor(220);
        doc.line(14, cursorY - 6, 200, cursorY - 6);
      }
    }

    /* ---------------------------------------------------------------------- */
    /* Grand totals                                                           */
    /* ---------------------------------------------------------------------- */

    const gtService = formatMoney(grandTotals.total_service_value || 0);
    const gtComm = formatMoney(grandTotals.total_commission || 0);

    if (cursorY > 260) {
      doc.addPage();
      cursorY = 18;
    }

    doc.setFontSize(12);
    doc.text(t("reports:commission.grandTotals"), 14, cursorY);

    autoTable(doc, {
      startY: cursorY + 4,
      head: [[t("reports:commission.totalServiceValuePln"), t("reports:commission.totalCommissionPln")]],
      body: [[gtService, gtComm]],
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fontStyle: "bold" },
      margin: { left: 14, right: 14 },
    });

    doc.save(`Commission_Report_${from}_${to}.pdf`);
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="commission-report-page">
      <div className="commission-report-header">
        <div className="header-left">
          <h1>{t("reports:commission.title")}</h1>

          {hasTiered && (
            <p className="header-hint">
              {t("reports:commission.tieredHintPrefix")} <strong>TIERED</strong>{" "}
              {t("reports:commission.tieredHintMiddle")} <em>{t("reports:commission.effectiveRateLower")}</em>{" "}
              {t("reports:commission.tieredHintSuffix")}
            </p>
          )}
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      {/* -------------------------------------------------------------------- */}
      {/* Filters bar                                                          */}
      {/* -------------------------------------------------------------------- */}
      <div className="filters-bar">
        <div className="filters-left">
          {/* Shared evaluation period framework.
              This mirrors the working Income Report pattern. */}
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {/* Employee filter remains page-specific.
              It is intentionally not part of the shared evaluation-period framework. */}
          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}
        </div>

        <div className="filters-actions">
          {/* IMPORTANT FOR LAYOUT:
              Portrait, Generate and Export PDF stay in one shared flex group.
              Because of that, JS structure is already correct for keeping
              Generate "attached" to Portrait and Export PDF attached to Generate.
              Any final visual distance tuning should be done in CSS. */}
          <div className="filter-item filter-item-pdf">
            <label className="filter-label">{t("reports:common.pdf")}</label>
            <select
              value={pdfOrientation}
              onChange={(e) => setPdfOrientation(e.target.value)}
              className="pdf-orientation"
            >
              <option value="p">{t("reports:common.portrait")}</option>
              <option value="l">{t("reports:common.landscape")}</option>
            </select>
          </div>

          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>

          {canExportPdf && hasReportData && !loading && (
            <button className="pdf-button" onClick={generatePDF}>
              {t("reports:common.exportPdf")}
            </button>
          )}
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Main local scroll area                                               */}
      {/* -------------------------------------------------------------------- */}
      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          reportData.map((userReport, idx) => {
            const mode = normalizeMode(userReport.commission_mode);

            const tierBreakdown = Array.isArray(userReport.tier_breakdown)
              ? userReport.tier_breakdown
              : [];

            const effectiveRate =
              userReport.effective_commission_rate !== undefined &&
              userReport.effective_commission_rate !== null
                ? formatPercent(userReport.effective_commission_rate)
                : formatPercent(userReport.services?.[0]?.commission_percentage || 0);

            return (
              <div className="employee-card" key={`${userReport.employee}-${idx}`}>
                <div className="employee-card-header">
                  <div className="employee-title">
                    <span className="employee-name">{userReport.employee}</span>
                    <span className={`mode-badge mode-${mode.toLowerCase()}`}>
                      {commissionModeLabel(mode)}
                    </span>
                  </div>

                  <div className="employee-metrics">
                    <div className="metric">
                      <span className="metric-label">{t("reports:commission.revenue")}</span>
                      <span className="metric-value">
                        {formatMoney(userReport.total_service_value)} PLN
                      </span>
                    </div>

                    <div className="metric">
                      <span className="metric-label">{t("reports:commission.commission")}</span>
                      <span className="metric-value">
                        {formatMoney(userReport.total_commission)} PLN
                      </span>
                    </div>

                    <div className="metric">
                      <span className="metric-label">{t("reports:commission.effectiveRate")}</span>
                      <span className="metric-value">{effectiveRate}%</span>
                    </div>
                  </div>
                </div>

                {/* ---------------------------------------------------------- */}
                {/* Tier breakdown                                             */}
                {/* ---------------------------------------------------------- */}
                {mode === "TIERED" && (
                  <div className="tier-breakdown-block">
                    <h3>{t("reports:commission.tierBreakdown")}</h3>

                    {tierBreakdown.length > 0 ? (
                      <div className="tier-table-wrapper">
                        <table className="tier-table">
                          <thead>
                            <tr>
                              <th>{t("reports:commission.tierFrom")}</th>
                              <th>{t("reports:commission.tierTo")}</th>
                              <th>{t("reports:commission.ratePercent")}</th>
                              <th>{t("reports:commission.baseInTierPln")}</th>
                              <th>{t("reports:commission.commissionPln")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tierBreakdown.map((t, i2) => (
                              <tr key={i2}>
                                <td>{t.min_amount ?? "0.00"}</td>
                                <td>
                                  {t.max_amount === null || t.max_amount === undefined
                                    ? "∞"
                                    : t.max_amount}
                                </td>
                                <td>{t.rate ?? "0.00"}</td>
                                <td>{t.base ?? "0.00"}</td>
                                <td>{t.commission ?? "0.00"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="tier-empty">
                        {t("reports:commission.tierBreakdownUnavailable")}
                      </p>
                    )}
                  </div>
                )}

                {/* ---------------------------------------------------------- */}
                {/* Services summary                                           */}
                {/* ---------------------------------------------------------- */}
                <div className="services-block">
                  <h3>{t("reports:commission.services")}</h3>

                  <div className="services-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t("reports:common.service")}</th>
                          <th>{t("reports:commission.serviceValuePln")}</th>
                          <th>{t("reports:commission.commissionPercent")}</th>
                          <th>{t("reports:commission.commissionPln")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(userReport.services || []).map((service, sIndex) => (
                          <tr key={sIndex}>
                            <td>{service.service}</td>
                            <td>{formatMoney(service.total_service_value)} PLN</td>
                            <td>{formatPercent(service.commission_percentage)}%</td>
                            <td>{formatMoney(service.commission_value)} PLN</td>
                          </tr>
                        ))}

                        <tr className="total-row">
                          <td><strong>{t("reports:common.total")}</strong></td>
                          <td><strong>{formatMoney(userReport.total_service_value)} PLN</strong></td>
                          <td />
                          <td><strong>{formatMoney(userReport.total_commission)} PLN</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Grand totals                                                       */}
        {/* ------------------------------------------------------------------ */}
        {hasReportData && !loading && (
          <div className="grand-totals-card">
            <div className="grand-title">{t("reports:commission.grandTotals")}</div>
            <div className="grand-metrics">
              <div className="grand-metric">
                <span className="grand-label">{t("reports:commission.totalServiceValue")}</span>
                <span className="grand-value">
                  {formatMoney(grandTotals.total_service_value)} PLN
                </span>
              </div>
              <div className="grand-metric">
                <span className="grand-label">{t("reports:commission.totalCommission")}</span>
                <span className="grand-value">
                  {formatMoney(grandTotals.total_commission)} PLN
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionReportPage;